import { backgroundColors } from '@/colors';
import Icon from '@/components/icons/Icon';
import { Z_INDEX } from '@/constants/styleConstants';
import { isServer } from '@/helpers';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const ANIMATION_DURATION_MS = 300;

// Open sheets in stacking order — Escape must close only the topmost.
const openSheets: symbol[] = [];

// Counted body lock: first sheet in saves styles + scrollY (it reads 0 under a fixed
// body), last one out restores — so stacked sheets can close in any order.
let scrollLockCount = 0;
let lockedScrollY = 0;
let prevBodyStyle = { position: '', top: '', width: '', backgroundColor: '' };

const acquireScrollLock = () => {
  scrollLockCount += 1;
  if (scrollLockCount > 1) return;
  const { style } = document.body;
  lockedScrollY = window.scrollY;
  prevBodyStyle = {
    position: style.position,
    top: style.top,
    width: style.width,
    backgroundColor: style.backgroundColor,
  };
  style.position = 'fixed';
  style.top = `-${lockedScrollY}px`;
  style.width = '100%';
  style.backgroundColor = backgroundColors.bg_neutral;
};

const releaseScrollLock = () => {
  scrollLockCount -= 1;
  if (scrollLockCount > 0) return;
  const { style } = document.body;
  style.position = prevBodyStyle.position;
  style.top = prevBodyStyle.top;
  style.width = prevBodyStyle.width;
  style.backgroundColor = prevBodyStyle.backgroundColor;
  window?.scrollTo(0, lockedScrollY);
};

/**
 * Back button on the left of the title bar; only rendered alongside `title`. The button is
 * icon-only, so `backLabel` (its accessible name) is required whenever `onBack` is passed.
 */
type BackProps = { onBack: () => void; backLabel: string } | { onBack?: never; backLabel?: never };

/**
 * A non-dismissible sheet has no close affordance and ignores the backdrop and Escape;
 * it is the visitor's only way forward, so focus is kept inside it too (as react-modal
 * does for the desktop modal) and handed back to where it came from on close.
 */
type DismissProps = { dismissible?: true; onClose: () => void } | { dismissible: false; onClose?: () => void };

type BottomSheetModalProps = {
  isOpen: boolean;
  title?: string;
  children: ReactNode;
} & BackProps &
  DismissProps;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  isOpen,
  onClose,
  title,
  onBack,
  backLabel,
  children,
  dismissible = true,
}) => {
  // isMounted keeps the DOM alive during the exit animation.
  // isVisible drives the CSS transition (translateY / opacity).
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [sheetId] = useState(() => Symbol('BottomSheetModal'));
  const sheetRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Registered while mounted, not while open: an exiting sheet stays topmost through its
  // slide-out, so a reflex second Escape is swallowed instead of closing the sheet below.
  useEffect(() => {
    if (!isMounted) return;
    openSheets.push(sheetId);
    return () => {
      const index = openSheets.indexOf(sheetId);
      if (index !== -1) openSheets.splice(index, 1);
    };
  }, [isMounted, sheetId]);

  // Mount → enter animation
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setIsMounted(false), ANIMATION_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Trigger enter animation one frame after mount so the transition fires
  useEffect(() => {
    if (!isMounted) return;
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [isMounted]);

  // iOS Safari touch-scrolls through `overflow: hidden`, so the body is frozen with
  // `position: fixed`; backgroundColor fills the gap behind the translucent toolbar.
  useEffect(() => {
    if (!isOpen) return;
    acquireScrollLock();
    return releaseScrollLock;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dismissible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openSheets[openSheets.length - 1] === sheetId) onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose, sheetId, dismissible]);

  // Read before the sheet's own content mounts, so it is the element that opened the sheet.
  useEffect(() => {
    if (!isOpen || dismissible) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [isOpen, dismissible]);

  useEffect(() => {
    if (!isMounted || dismissible) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    const focusable = () => Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (!sheet.contains(document.activeElement)) (focusable()[0] ?? sheet).focus();

    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = sheet.contains(document.activeElement);
      const atEdge = e.shiftKey ? document.activeElement === first : document.activeElement === last;
      if (!inside || atEdge) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      document.removeEventListener('keydown', trap);
      openerRef.current?.focus();
    };
  }, [isMounted, dismissible]);

  if (!isMounted || isServer) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="bg-overlay-50 fixed inset-0"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${ANIMATION_DURATION_MS}ms ease`,
          zIndex: Z_INDEX.BOTTOM_SHEET_BACKDROP,
        }}
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={dismissible ? undefined : -1}
        className="bg-surface_l4 fixed inset-x-0 bottom-0 flex max-h-[90dvh] flex-col overflow-hidden rounded-t-lg"
        style={{
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${ANIMATION_DURATION_MS}ms ease`,
          zIndex: Z_INDEX.BOTTOM_SHEET,
        }}>
        {title && (
          <div className="bg-surface_l1 px-xs py-md relative flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                onMouseDown={(e) => e.preventDefault()}
                aria-label={backLabel}
                className="left-sm absolute outline-none">
                <Icon color="fg_on_surface_l1" variant="back" style={{ height: 20, width: 20 }} />
              </button>
            )}
            <div className="flex flex-1 justify-center">
              <h2 className="text-h-s text-fg_on_surface_l1">{title}</h2>
            </div>
            {dismissible && (
              <button
                onClick={onClose}
                onMouseDown={(e) => e.preventDefault()}
                className="right-sm absolute outline-none">
                <Icon color="fg_on_surface_l1" variant="close-alt" style={{ height: 20, width: 20 }} />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </>,
    document.body,
  );
};

export default BottomSheetModal;
